import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef } from "react";

const AnimatedCounter = ({ value, suffix = "", duration = 2 }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest));
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          animate(count, parseInt(value), { duration });
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [count, value, duration]);

  return (
    <span ref={ref} className="stat-number">
      <motion.span>{rounded}</motion.span>
      {suffix}
    </span>
  );
};

const Stats = () => {
  const stats = [
    { id: 1, value: "30", suffix: "+", label: "Projects Completed" },
    { id: 2, value: "3", suffix: "+", label: "Years Experience" },
    { id: 3, value: "95", suffix: "%", label: "Client Retention Rate" },
    { id: 4, value: "37", suffix: "%", label: "Increased Conversions" },
  ];

  return (
    <section className="relative py-24 md:py-32 bg-[#0A0A0A] border-y border-white/5 overflow-hidden">
      {/* Geometric elements */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="absolute" style={{ left: '3%', top: '10%', width: 200, height: 200 }} viewBox="0 0 200 200">
          <path d="M 180 100 A 80 80 0 0 1 20 100" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
        </svg>
        <div className="absolute h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" style={{ width: '25%', right: '10%', top: '25%' }} />
        <div className="absolute border border-white/[0.05]" style={{ width: 18, height: 18, right: '15%', bottom: '20%', transform: 'rotate(45deg)' }} />
        <svg className="absolute" style={{ right: '5%', top: '40%', width: 30, height: 30 }} viewBox="0 0 30 30">
          <path d="M 10 25 L 25 15 L 10 5" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        </svg>
        <div className="absolute w-2 h-2 rounded-full bg-purple-400/15" style={{ left: '8%', bottom: '25%' }} />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm font-mono text-blue-400 tracking-wider uppercase mb-4">
            Highlights
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] tracking-tight text-white">
            Numbers that{" "}
            <span className="text-gradient">drive success</span>
          </h2>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
          data-testid="stats-grid"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center"
              data-testid={`stat-${stat.id}`}
            >
              <div className="text-5xl md:text-6xl lg:text-7xl font-bold font-['Syne'] mb-3">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              </div>
              <p className="text-sm md:text-base text-white/50 font-medium">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-20 text-center max-w-4xl mx-auto"
        >
          <p className="text-xl md:text-2xl lg:text-3xl text-white/80 font-medium leading-relaxed">
            Blending{" "}
            <span className="text-gradient font-semibold">cutting-edge technology</span>{" "}
            with{" "}
            <span className="text-gradient font-semibold">strategic design</span>{" "}
            to build memorable online identities that capture hearts and drive growth.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Stats;
